import type { FormatLocale } from '#layers/base/public';

// schedule-x throws on any locale that is not a bare `language-REGION` tag.
const SCHEDULE_X_LOCALE_PATTERN = /^[a-z]{2}-[A-Z]{2}$/;
const FALLBACK_SCHEDULE_X_LOCALE = 'en-US';

// schedule-x looks its strings up by their English text, so each key must match its source exactly.
const ScheduleXTranslationKeys = {
	OneMoreEvent: '+ 1 event',
	MoreEvents: '+ {{n}} events',
	OneMoreEventLabel: 'Link to 1 more event on {{date}}',
	MoreEventsLabel: 'Link to {{n}} more events on {{date}}',
	TimeRangeSeparator: 'to',
	AllDayEvents: 'Full day- and multiple day events',
} as const;

/** Narrows a locale to the `language-REGION` tag schedule-x accepts, guessing a missing or numeric region. */
export function toScheduleXLocale(formatLocale: FormatLocale): string {
	const { language, region } = new Intl.Locale(formatLocale).maximize();
	const twoLetterRegion = region && /^[A-Z]{2}$/.test(region) ? region : new Intl.Locale(language).maximize().region;
	const tag = `${language}-${twoLetterRegion}`;

	return SCHEDULE_X_LOCALE_PATTERN.test(tag) ? tag : FALLBACK_SCHEDULE_X_LOCALE;
}

type Translate = (key: string, named?: Record<string, unknown>, plural?: number) => string;

/** schedule-x looks the strings up by its locale without the hyphen, and fills in `{{n}}` and `{{date}}` itself. */
export function buildScheduleXTranslations(t: Translate, scheduleXLocale: string) {
	return {
		[scheduleXLocale.replaceAll('-', '')]: {
			[ScheduleXTranslationKeys.OneMoreEvent]: t('layout.calendar.more_items', { n: 1 }),
			[ScheduleXTranslationKeys.MoreEvents]: t('layout.calendar.more_items', { n: '{{n}}' }),
			[ScheduleXTranslationKeys.OneMoreEventLabel]: t('layout.calendar.more_items_label', { n: 1, date: '{{date}}' }, 1),
			[ScheduleXTranslationKeys.MoreEventsLabel]: t('layout.calendar.more_items_label', { n: '{{n}}', date: '{{date}}' }, 2),
			[ScheduleXTranslationKeys.TimeRangeSeparator]: t('layout.calendar.time_range_separator'),
			[ScheduleXTranslationKeys.AllDayEvents]: t('layout.calendar.all_day_items'),
		},
	};
}
